import { Feature, HeightOfTongue, heightOfTongue, mannerOfArticulation, partOfTongue, placeOfArticulation, roundedness, voicing } from "./features";
import { DiacriticFeatures, DiacriticsToFeatures, getIpaDiacriticFeature, SymbolsToFeatures } from "./ipa";

/** figure out the set of features these symbols represent */
export function symbolsToFeatures(
  symbols: string,
  lookup: SymbolsToFeatures,
  diacritics: DiacriticsToFeatures
): Feature[] | undefined {
  if (symbols === undefined) {
    return undefined;
  }
  if (symbols.length <= 1) {
    const features = lookup[symbols];
    if (features !== undefined) {
      return features;
    }
    const dia = getIpaDiacriticFeature(symbols);
    return dia === '' ? undefined : [dia];
  }

  // first check for leading diacritics like ʰ
  const [features1, index1] = addDiacritics([], symbols, 0, diacritics);
  // resolve base symbol
  const result = getSymbolFeatures(symbols, index1, lookup);
  if (result === undefined) {
    return undefined;
  }
  const [features2, index2] = result;
  // check if there are multiple tones
  if (features2.includes('tone')) {
    const toneFeatures = getToneFeatures(symbols, features2, lookup);
    if (toneFeatures) {
      return toneFeatures;
    }
  }
  // add trailing diacritics, which may modify base symbol features
  const [features23] = addDiacritics(features2, symbols, index2, diacritics);
  return features1.length === 0 ? features23 : features1.concat(features23);
}

/** get the features for the longest base symbol, e.g., t̠ʃ, tʃ, or t */
function getSymbolFeatures(
  symbols: string, index: number, lookup: SymbolsToFeatures
): [Feature[], number] | undefined {
  const fullLength = symbols.length;
  for (let baseLength = 4; baseLength >= 1; baseLength--) {
    if (fullLength >= baseLength) {
      const pair = lookup[symbols.substring(index, baseLength)];
      if (pair !== undefined) {
        return [pair, baseLength];
      }
    }
  }
  return undefined;
}

/**
 * keep adding features as long as there are valid diacritics,
 * returns [modified features, index where it stopped]
 */
function addDiacritics(
  features: Feature[], symbols: string, index: number, diacritics: DiacriticsToFeatures
): [Feature[], number] {
  for (let i = index; i < symbols.length; i++) {
    const extra = diacritics[symbols.substring(i, i + 1)];
    if (extra === undefined) {
      index = i;
      break;  // not a valid diacritic, so stop
    }
    features = addDiacritic(features, extra);
  }
  return [features, index];
}

/** add a diacritic's features based on the symbol features */
function addDiacritic(features: Feature[], diacritic: DiacriticFeatures): Feature[] {
  const feature = getDiacriticFeature(features, diacritic);
  if (feature === null) {
    return features;
  } else if (feature === 'lowered') {
    return lowerFeatures(features);
  } else if (feature === 'raised') {
    return raiseFeatures(features);
  } else if (feature === 'ejective') {
    return makeEjective(features);
  }
  const filtered = removeSameClass(features, feature);
  return filtered.concat(feature);
}

function getDiacriticFeature(features: Feature[], diacritic: DiacriticFeatures): Feature | null {
  const isVowel = features.includes('vowel');
  if (diacritic.vowel !== undefined && isVowel) {
    return diacritic.vowel;
  }
  if (diacritic.consonant !== undefined && !isVowel) {
    return diacritic.consonant;
  }
  if (diacritic.other !== undefined) {
    return diacritic.other;
  }
  return null;
}

/** remove anything from the same class of feature as the diacritic */
function removeSameClass(features: Feature[], feature: Feature): Feature[] {
  for (const featureClass of featureClasses) {
    if (featureClass.includes(feature)) {
      return features.filter(f => !featureClass.includes(f));
    }
  }
  return features;
}

// sets of mutually exclusive features
const featureClasses: Feature[][] = [
  placeOfArticulation, mannerOfArticulation, voicing,
  heightOfTongue, partOfTongue, roundedness
];

/**
 * either modify an existing feature to make it lowered (e.g. close-mid > mid) or add the 'lowered' feature.
 * note that this is currently limited to fricatives & vowels
 * because often the lowered versions don't have dedicated IPA, e.g., ð̞ e̞
 */
function lowerFeatures(features: Feature[]): Feature[] {
  let hasMod = false;
  const mod = features.map(f => {
    if (f === 'fricative') {
      hasMod = true;
      return 'approximant';
    }

    const index = heightOfTongue.indexOf(f as HeightOfTongue);
    if (index !== -1) {
      const changed = heightOfTongue[index + 1];
      if (changed !== undefined) {
        hasMod = true;
        return changed;
      }
    }
    return f;
  });
  return hasMod ? mod : mod.concat('lowered');
}
function raiseFeatures(features: Feature[]): Feature[] {
  let hasMod = false;
  const mod = features.map(f => {
    if (f === 'approximant') {
      hasMod = true;
      return 'fricative';
    }

    const index = heightOfTongue.indexOf(f as HeightOfTongue);
    if (index !== -1) {
      const changed = heightOfTongue[index - 1];
      if (changed !== undefined) {
        hasMod = true;
        return changed;
      }
    }
    return f;
  });
  return hasMod ? mod : mod.concat('raised');
}

function makeEjective(features: Feature[]): Feature[] {
  const changed = features.map(f => f === 'pulmonic' ? 'non-pulmonic' : f);
  return changed.concat('ejective');
}

/** if multiple tones, add on a list of the tone levels to the base features */
function getToneFeatures(symbols: string, features: Feature[], lookup: SymbolsToFeatures): Feature[] | null {
  if (symbols.length < 2) {
    return null;
  }
  let toneFeatures = features;
  for (let i = 1; i < symbols.length; i++) {
    const f = lookup[symbols[i]];
    if (!f.includes('tone')) {
      return null;
    }
    // keep adding on the last feature, which we assume is the tone level
    toneFeatures = toneFeatures.concat(f[f.length - 1]);
  }
  return toneFeatures;
}
